import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";
import AdaptiveSection from "@/components/AdaptiveSection";
import AdaptiveCta from "@/components/AdaptiveCta";
import GlowCta from "@/components/GlowCta";
import HomeHero from "@/components/home/HomeHero";
import FocusedImage from "@/components/FocusedImage";

import PageSeo from "@/components/PageSeo";
import { organizationJsonLd } from "@/lib/seo";
import ServiceCard from "@/components/ServiceCard";
import GalleryTeaser from "@/components/GalleryTeaser";
import aboutImage from "@/assets/over-ons-deken.jpg";
import practiceImage from "@/assets/sprankelpraktijk.webp";
import zomerkampImage from "@/assets/zomerkampjes.webp";
import yogaImage from "@/assets/little-me-time-meditatie.jpg";

const Home = () => {
  return (
    <>
      <PageSeo
        title="Praktijk Sprankelien — Speltherapie & Yoga in Ramsel"
        description="Praktijk Sprankelien in Ramsel biedt warme, professionele begeleiding voor kinderen. Speltherapie, kinderyoga en zomerkampen in een veilige, huiselijke sfeer."
        jsonLd={organizationJsonLd}
      />
      <HomeHero />

      {/* Hero-to-content bridge */}
      <div className="hero-bridge" />

      {/* CTAs — between hero and "Over ons" */}
      <div className="hidden min-[769px]:flex flex-col sm:flex-row items-center justify-center gap-3.5 sm:gap-5 pt-8 pb-16 sm:py-16 px-6">
        <GlowCta to="/contact">Neem contact op</GlowCta>
        {/* Scroll-anchor CTA: springt soepel naar de Over-ons-sectie. Werkt ook
            in de Lovable dev-iframe (waar hash-routes soms 'stuck' aanvoelen)
            doordat we expliciet scrollIntoView aanroepen op het ankerelement. */}
        <a
          href="#over-ons"
          onClick={(e) => {
            e.preventDefault();
            const el = document.getElementById("over-ons");
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
              // Update URL zonder router-navigatie zodat de pagina niet verspringt.
              history.replaceState(null, "", "#over-ons");
            }
          }}
          className="btn-premium relative overflow-hidden inline-block bg-primary-foreground text-primary px-10 py-[1.15rem] sm:py-3.5 text-[15px] sm:text-[13px] min-h-[52px] sm:min-h-0 rounded-full transition-transform duration-[0.5s] ease-[cubic-bezier(0.22,0.68,0.35,1)] hover:scale-[1.025] active:scale-[0.97]"
        >
          <span className="absolute inset-0 -translate-x-full animate-[shimmer_4.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/12 to-transparent" />
          <span className="relative flex items-center gap-2.5">
            Ontdek wie we zijn <ArrowRight className="h-4 w-4" />
          </span>
        </a>
      </div>

      {/* Scroll-anker voor 'Over ons'. scroll-mt compenseert de fixed navbar
          zodat de sectiekop niet onder de balk valt na het springen. */}
      <div id="over-ons" aria-hidden="true" className="scroll-mt-24 sm:scroll-mt-28" />

      {/* Welcome — Editorial spread: text left, image right */}
      <AdaptiveSection intensity={0.5}><section className="relative py-12 min-[769px]:py-32 md:py-44 lg:py-56 px-5 min-[769px]:px-6 overflow-hidden">
        <div className="container-narrow">
          <div className="grid grid-cols-1 min-[769px]:lg:grid-cols-12 gap-8 min-[769px]:gap-16 lg:gap-20 items-center">
            <AnimatedSection gentle className="order-2 min-[769px]:order-1 lg:col-span-5">
              <p className="text-[11px] text-muted-foreground/40 font-body tracking-[0.14em] uppercase mb-5 sm:mb-8">
                Over ons
              </p>
              <h2 className="font-heading text-[1.75rem] min-[431px]:text-[1.9rem] min-[769px]:text-[2rem] lg:text-[2.5rem] text-foreground mb-5 min-[769px]:mb-10 leading-[1.15] tracking-tight">
                Een warme plek voor kinderen in Ramsel
              </h2>
              <div className="space-y-4 min-[769px]:space-y-7 text-[16px] min-[769px]:text-[14px] text-foreground/82 min-[769px]:text-muted-foreground/45 font-body leading-[1.6] min-[769px]:leading-[2.1] max-w-none min-[769px]:max-w-[380px] mb-7 min-[769px]:mb-16">
                <p>
                  Bij Praktijk Sprankelien in Ramsel bieden we een veilige en huiselijke
                  omgeving waar kinderen zichzelf mogen zijn. Met zachtheid, creativiteit
                  en aandacht begeleiden we elk kind op zijn eigen tempo.
                </p>
                <p>
                  Of het nu gaat om het verwerken van emoties, het opbouwen van
                  zelfvertrouwen of het vinden van innerlijke rust — wij zijn er om
                  samen te sprankelen.
                </p>
              </div>
              <Link
                to="/over-mij"
                className="inline-flex items-center gap-2.5 text-[12px] font-semibold text-accent min-[769px]:text-accent/60 font-body uppercase tracking-[0.12em] group hover:text-accent transition-colors duration-[0.6s] ease-[cubic-bezier(0.22,0.68,0.35,1)]"
              >
                Meer over ons
                <ArrowRight className="h-3.5 w-3.5 transition-all duration-[0.6s] ease-[cubic-bezier(0.22,0.68,0.35,1)] group-hover:translate-x-1.5" />
              </Link>
            </AnimatedSection>

            <AnimatedSection delay={0.15} gentle className="order-1 min-[769px]:order-2 lg:col-span-7">
              <div className="relative min-[769px]:lg:pl-8">
                <div className="rounded-[1.5rem] sm:rounded-[1.75rem] overflow-hidden"
                  style={{ boxShadow: "0 8px 40px -12px hsl(var(--foreground) / 0.08), 0 2px 12px -4px hsl(var(--foreground) / 0.03)" }}
                >
                  <FocusedImage
                    src={aboutImage}
                    alt="Meisje gluurt warm lachend onder een zachte deken in de yogaruimte van Praktijk Sprankelien"
                    loading="lazy"
                    width={1200}
                    height={800}
                    focal={{
                      base: [50, 35],
                      sm: [50, 32],
                      md: [50, 30],
                      lg: [50, 50],
                      xl: [50, 48],
                      "2xl": [50, 46],
                    }}
                    className="w-full aspect-[4/5] min-[769px]:aspect-[5/6] md:aspect-[4/5] lg:aspect-[3/4]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/10 via-transparent to-transparent pointer-events-none rounded-[1.5rem] sm:rounded-[1.75rem]" />
                  <div className="absolute inset-0 rounded-[1.5rem] sm:rounded-[1.75rem] ring-1 ring-inset ring-foreground/[0.03] pointer-events-none" />
                </div>
                <div
                  className="hidden md:block absolute -bottom-6 -left-2 lg:-left-6 bg-background/95 backdrop-blur-sm rounded-[1.25rem] border border-border/8 px-7 py-6 max-w-[200px]"
                  style={{ boxShadow: "0 6px 28px -8px hsl(var(--foreground) / 0.06), 0 1px 4px -1px hsl(var(--foreground) / 0.02)" }}
                >
                  <p className="font-heading italic text-[1.05rem] text-foreground/60 leading-snug tracking-tight">
                    "Groei vanuit zachtheid"
                  </p>
                </div>
                <p className="min-[769px]:hidden font-heading italic text-center text-[1.15rem] text-foreground/75 leading-snug tracking-tight mt-5">
                  "Groei vanuit zachtheid"
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section></AdaptiveSection>

      {/* Core Belief — Full-bleed typographic pause */}
      <section className="relative py-12 min-[769px]:py-40 md:py-52 lg:py-60 px-5 min-[769px]:px-6 bg-secondary overflow-hidden">
        <div className="max-w-[90%] min-[769px]:max-w-[520px] mx-auto">
          <AnimatedSection gentle>
            <p className="font-heading italic text-[1.5rem] min-[769px]:text-[1.65rem] md:text-[1.85rem] lg:text-[2.2rem] text-foreground/88 min-[769px]:text-foreground/75 leading-[1.45] tracking-tight text-center">
              "Wij geloven in de kracht van elk kind en bieden een
              ondersteunende omgeving waar ze kunnen groeien."
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Aanbod — Staggered editorial grid, not uniform columns */}
      <AdaptiveSection intensity={0.5}><section className="relative py-12 min-[769px]:section-padding-lg px-5 min-[769px]:px-6 overflow-hidden">
        <div className="container-narrow">
          <AnimatedSection className="mb-4 min-[769px]:mb-10" gentle>
            <p className="text-[11px] text-accent min-[769px]:text-muted-foreground/30 font-body tracking-[0.14em] uppercase text-center mb-3 min-[769px]:mb-5">
              Ons aanbod
            </p>
              <h2 className="font-heading text-[1.75rem] min-[431px]:text-[1.9rem] min-[769px]:text-[2rem] lg:text-[2.6rem] text-foreground tracking-tight text-center">
               Ons aanbod in Ramsel
              </h2>
          </AnimatedSection>
          <AnimatedSection className="mb-7 min-[769px]:mb-32" gentle>
            <p className="text-[16px] min-[769px]:text-[15px] text-foreground/78 min-[769px]:text-muted-foreground/40 font-body leading-[1.6] min-[769px]:leading-[2] text-center max-w-[28rem] mx-auto">
              Drie manieren waarop wij kinderen begeleiden naar meer vertrouwen, rust en verbinding.
            </p>
          </AnimatedSection>
          <div className="grid grid-cols-1 min-[769px]:md:grid-cols-3 gap-6 min-[769px]:gap-10 md:gap-8 lg:gap-12">
            <ServiceCard
              title="Sprankelpraktijk"
              description="Persoonlijke begeleiding voor kinderen die worstelen met emoties, stress of zelfvertrouwen. In Ramsel."
              image={practiceImage}
              to="/sprankelpraktijk"
              label="Therapie & begeleiding"
              delay={0}
            />
            <ServiceCard
              title="Little me time"
              description="Kinderyoga en ontspanning — een moment van rust en verbinding met zichzelf."
              image={yogaImage}
              to="/little-me-time"
              label="Yoga & ontspanning"
              delay={0.12}
            />
            <ServiceCard
              title="Zomerkampen"
              description="Avontuurlijke dagen in de natuur bij Ramsel met aandacht voor groei, creativiteit en verbinding."
              image={zomerkampImage}
              to="/zomerkampen"
              label="Kampen & beleving"
              delay={0.24}
            />
          </div>
          <AnimatedSection className="text-center mt-8 min-[769px]:mt-36" gentle delay={0.3}>
            <AdaptiveCta>
              <GlowCta to="/contact">
                Neem contact op
              </GlowCta>
            </AdaptiveCta>
            <p className="text-[11px] text-muted-foreground/50 min-[769px]:text-muted-foreground/25 font-body mt-4 min-[769px]:mt-6 tracking-[0.1em] uppercase">
              Vrijblijvend kennismakingsgesprek
            </p>
          </AnimatedSection>
        </div>
      </section></AdaptiveSection>

      {/* Values — Editorial pillars with clear hierarchy */}
      <section className="py-12 min-[769px]:py-32 md:py-44 lg:py-56 px-5 min-[769px]:px-6 bg-secondary relative overflow-hidden">
        <div className="container-narrow">
          <AnimatedSection className="mb-6 min-[769px]:mb-20 md:mb-28" gentle>
            <p className="text-[11px] text-accent min-[769px]:text-muted-foreground/30 font-body tracking-[0.14em] uppercase text-center mb-3 min-[769px]:mb-5">
              Onze waarden
            </p>
            <h2 className="font-heading text-[1.75rem] min-[431px]:text-[1.9rem] min-[769px]:text-[2rem] lg:text-[2.6rem] text-foreground tracking-tight text-center">
              Waar Sprankelien voor staat
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 min-[769px]:md:grid-cols-3 gap-4 min-[769px]:gap-16 md:gap-10 lg:gap-16">
            {[
              {
                step: "01",
                title: "Warme begeleiding",
                text: "Elk kind verdient een veilige plek waar het gezien en gehoord wordt. Wij bieden die geborgenheid.",
              },
              {
                step: "02",
                title: "Positieve groei",
                text: "We focussen op krachten en mogelijkheden, niet op problemen. Elk kind groeit op zijn eigen tempo.",
              },
              {
                step: "03",
                title: "Verbinding",
                text: "Tussen kind en ouder, tussen lichaam en geest, tussen gevoel en taal. Samen bouwen we bruggen.",
              },
            ].map((v, i) => (
              <AnimatedSection key={v.title} delay={i * 0.12} gentle>
                <div className="value-card-mobile text-left md:text-left">
                  <div className="flex items-baseline gap-3 mb-3 md:mb-0 md:block">
                    <p className="text-[12px] text-accent/80 min-[769px]:text-accent font-body font-semibold tracking-[0.18em] uppercase md:mb-6">
                      {v.step}
                    </p>
                    <div className="hidden md:block w-10 h-px bg-gradient-to-r from-accent/30 to-transparent mb-8 mx-0" />
                    <h3 className="font-heading text-[1.32rem] min-[431px]:text-[1.4rem] text-foreground tracking-tight leading-[1.2] md:mb-5">
                      {v.title}
                    </h3>
                  </div>
                  <p className="text-[16px] min-[769px]:text-[13.5px] text-foreground/82 min-[769px]:text-muted-foreground/55 md:text-muted-foreground/45 font-body leading-[1.6] min-[769px]:leading-[2] max-w-none md:max-w-[300px] mx-0">
                    {v.text}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Teaser — Visual proof, warm engagement */}
      <AdaptiveSection intensity={0.5}>
        <GalleryTeaser />
      </AdaptiveSection>

      {/* CTA — The closing whisper */}
      <section className="relative py-12 min-[769px]:py-44 md:py-56 lg:py-72 px-5 min-[769px]:px-6 overflow-hidden bg-secondary">
        <div className="max-w-[400px] mx-auto text-center">
           <AnimatedSection gentle>
             <p className="text-[12px] text-accent min-[769px]:text-muted-foreground/30 font-body mb-4 min-[769px]:mb-12 tracking-[0.12em] uppercase">
               Een eerste gesprek is altijd gratis
             </p>
             <h2 className="font-heading italic text-[1.75rem] min-[431px]:text-[1.9rem] min-[769px]:text-[2rem] lg:text-[2.5rem] text-foreground mb-6 min-[769px]:mb-20 tracking-tight leading-[1.2]">
               Klaar om samen te Sprankelen?
             </h2>
             <AdaptiveCta>
               <GlowCta to="/contact">
                 Neem contact op
               </GlowCta>
             </AdaptiveCta>
           </AnimatedSection>
        </div>
      </section>
    </>
  );
};

export default Home;
